import classnames from 'classnames'
import * as React from 'react'
import * as Reakit from 'reakit/Popover'
import { Portal } from 'reakit/Portal'

import { usePopoverMaxHeight } from '../../../hooks/use-popover-maxHeight'
import { useWindowSize } from '../../../hooks/use-window-size'
import { Box, BoxProps } from '../../box'
import { getExtendedDialogState, useZIndex } from '../../dialog'
import { PopoverStateReturn } from './hooks'

export interface PopoverProps extends BoxProps, Pick<Reakit.PopoverProps, 'hide'> {
  /**
   * The returned object from `usePopoverState`
   */
  state: PopoverStateReturn

  /**
   * Focus the popover when it opens
   *
   * @default true
   */
  focusOnShow?: Reakit.PopoverProps['unstable_autoFocusOnShow']

  /**
   * Focus the anchor element when it closes
   *
   * @default true
   */
  focusOnHide?: Reakit.PopoverProps['unstable_autoFocusOnHide']

  /**
   * Focus the anchor element when it closes
   *
   * @default undefined
   */
  finalFocusRef?: Reakit.PopoverProps['unstable_finalFocusRef']

  /**
   * Hides when clicked outside of popover, works with changing focus from the trigger component to another one
   *
   * @default true
   */
  hideOnClickOutside?: Reakit.PopoverProps['hideOnClickOutside']

  /**
   * Overlay click callback or if false is provided, overlay click calls hide func on popover state object
   *
   * @default undefined
   */
  onOverlayClick?: (() => void) | false

  /**
   * Popover wrapper css style object
   *
   * @default undefined
   */
  popoverWrapperStyle?: React.CSSProperties

  /**
   * Show popover arrow
   *
   * @default false
   */
  hasArrow?: boolean

  /**
   * Enable or disable backdrop click events
   *
   * @default true
   */
  hasBackdrop?: boolean

  /**
   * Set the popover to height to be limited by the viewport instead of having full height
   *
   * @default false
   */
  hasViewportHeightLimit?: boolean

  /**
   * Set the popover max-width to be limited by the trigger button
   */
  hasButtonWidthLimit?: boolean

  /**
   * Can be used together with `hasViewportHeightLimit` to limit the height of the popover
   *
   * @default undefined
   */
  popoverMaxHeight?: number
}

export function Popover({
  state,
  children,
  focusOnShow = true,
  focusOnHide = true,
  finalFocusRef,
  'aria-label': ariaLabel,
  hide,
  hideOnClickOutside = true,
  hasBackdrop = true,
  onOverlayClick,
  popoverWrapperStyle,
  popoverMaxHeight = Number.MAX_SAFE_INTEGER,
  theme = 'light',
  hasViewportHeightLimit = false,
  hasButtonWidthLimit = false,
  ...props
}: PopoverProps) {
  const zIndex = useZIndex()
  const { isFullyClosed } = getExtendedDialogState(state)
  const { innerHeight } = useWindowSize({ isWatching: !isFullyClosed })

  const buttonElement = state.unstable_referenceRef.current
  const maxHeightStyle = usePopoverMaxHeight({
    maxHeightLimit: popoverMaxHeight,
    hasViewportHeightLimit,
    offsets: state.offsets,
    state,
  })

  React.useEffect(() => {
    if (!buttonElement || !state.visible || !(hasViewportHeightLimit || hasButtonWidthLimit)) return

    const buttonRect = buttonElement.getBoundingClientRect()
    const popoverId = buttonElement.getAttribute('aria-controls')
    const popoverWrapper = popoverId ? document.getElementById(popoverId) : null

    // Limit popover max-width to be width of the trigger button
    if (hasButtonWidthLimit) {
      popoverWrapper?.style.setProperty('max-width', `${buttonRect.width}px`)
    }
  }, [
    state.visible,
    state.offsets,
    buttonElement,
    hasViewportHeightLimit,
    hasButtonWidthLimit,
    innerHeight,
    state.placement,
    popoverMaxHeight,
  ])

  if (isFullyClosed) return null

  return (
    <Portal>
      <Reakit.Popover
        {...state}
        hide={hide ?? state.hide}
        hideOnClickOutside={hideOnClickOutside}
        unstable_autoFocusOnShow={focusOnShow}
        aria-label={ariaLabel}
        unstable_autoFocusOnHide={focusOnHide}
        unstable_finalFocusRef={finalFocusRef}
      >
        {({ onTransitionEnd, onAnimationEnd, ...innerProps }) => (
          <>
            <div
              className={classnames(zIndex, 'fixed bottom-0 left-0 right-0 top-0', {
                'pointer-events-none': !hasBackdrop,
              })}
              onClick={onOverlayClick === false ? hide ?? state.hide : onOverlayClick}
              onMouseDown={(event) => {
                // prevents from page scroll when clicked outside of popover
                event.preventDefault()
              }}
            />
            <div
              {...innerProps}
              className={classnames(zIndex, 'finviz-popover pointer-events-none outline-none')}
              style={{ ...innerProps.style, ...popoverWrapperStyle }}
            >
              <Box
                onTransitionEnd={onTransitionEnd}
                onAnimationEnd={onAnimationEnd}
                data-placement={state.placement}
                theme={theme}
                {...props}
                className={classnames('pointer-events-auto', props.className)}
                style={hasViewportHeightLimit ? { ...maxHeightStyle, ...props.style } : props.style}
              >
                {children}
              </Box>
            </div>
          </>
        )}
      </Reakit.Popover>
    </Portal>
  )
}
